import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Scramble } from '../../brand/Scramble';
import { splitPatterns } from '../../components/admin/AccessEditor';
import { DraftPreview, HtmlInput } from '../../components/admin/HtmlInput';
import { pricingBody, PricingFields, type PricingDraft } from '../../components/admin/PricingFields';
import { api, errorMessage } from '../../lib/api';
import { todayIso } from '../../lib/format';

const slugify = (t: string) =>
  t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

// the <title> of a coded proposal is usually "Título · Proposta Ringa"
const titleFromHtml = (html: string): string => {
  const m = html.match(/<title>([^<]*)<\/title>/i);
  return m ? m[1].split(/\s+[·|–-]\s+/)[0].trim() : '';
};

export const ProposalNew = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [client, setClient] = useState('');
  const [slug, setSlug] = useState('');
  const [expiresOn, setExpiresOn] = useState(todayIso(30));
  const [html, setHtml] = useState('');
  const [access, setAccess] = useState('');
  const [invite, setInvite] = useState(true);
  const [pricing, setPricing] = useState<PricingDraft>({ engineer: '', team: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!html) return setError('Envie o HTML da proposta.');
    const prices = pricingBody(pricing);
    if ('error' in prices) return setError(prices.error);
    setBusy(true);
    setError('');
    try {
      const r = await api<{ id: string }>('/api/admin/proposals', {
        body: { title, clientName: client, slug: slug || undefined, expiresOn, html, access: splitPatterns(access), invite, ...prices },
      });
      navigate(`/admin/propostas/${r.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="ahead">
        <div>
          <Link to="/admin/propostas" className="back">
            ← Propostas
          </Link>
          <h1>Nova proposta.</h1>
        </div>
      </div>
      <form className="new-grid" onSubmit={submit}>
        <div className="panel panel-pad new-form">
          <div className="field">
            <span>HTML da proposta</span>
            <HtmlInput
              value={html}
              onChange={(h) => {
                setHtml(h);
                if (!title) setTitle(titleFromHtml(h));
              }}
            />
          </div>
          <label className="field">
            <span>Título</span>
            <input className="input" required minLength={2} maxLength={160} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sioon de volta à obra" />
          </label>
          <div className="two">
            <label className="field">
              <span>Cliente</span>
              <input className="input" required maxLength={120} value={client} onChange={(e) => setClient(e.target.value)} placeholder="Sioon" />
            </label>
            <label className="field">
              <span>Válida até</span>
              <input className="input" type="date" required min={todayIso(0)} value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            </label>
          </div>
          <div className="field">
            <label htmlFor="slug">Endereço</label>
            <div className="slug">
              <span className="dim">/p/</span>
              <input
                id="slug"
                className="input"
                value={slug}
                maxLength={60}
                aria-describedby="slug-help"
                onChange={(e) => setSlug(slugify(e.target.value))}
                placeholder={slugify(title) || 'gerado-do-titulo'}
              />
            </div>
            <small id="slug-help">O cliente sempre entra por metron.ringalabs.com.br; este endereço leva direto à proposta depois do código.</small>
          </div>
          <div className="field">
            <span>Valores mensais</span>
            <PricingFields value={pricing} onChange={setPricing} />
          </div>
          <label className="field">
            <span>Quem pode abrir</span>
            <textarea
              className="textarea"
              style={{ minHeight: 84 }}
              value={access}
              onChange={(e) => setAccess(e.target.value)}
              placeholder={'ana@cliente.com.br\n@cliente.com.br  (libera o domínio inteiro)'}
            />
          </label>
          <label className="check">
            <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
            <span>Enviar convite por e-mail agora para os endereços acima</span>
          </label>
          {error && <p className="error">{error}</p>}
          <div className="dialog-foot">
            <Link to="/admin/propostas" className="btn btn-ghost">
              Cancelar
            </Link>
            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Criando…' : <Scramble>Criar proposta</Scramble>}
            </button>
          </div>
        </div>
        <div className="new-preview">
          <p className="dim small">Pré-visualização, como o cliente vai ver</p>
          <DraftPreview html={html} />
        </div>
      </form>
    </>
  );
};
